import { useMeetingsContext, useMeetingsRealtimeFeaturesEnabled } from 'features/meetings'
import { useScope } from 'hooks/meetings/use_scope'

import { getWarningKey, type WarningKey } from '../utils'
import { type Meeting } from '../utils/meeting-helpers'

/**
 * Hook to determine if a meeting should display a warning indicator.
 *
 * Uses MeetingsContext for efficient batch lookup (list views), or calculates
 * independently when otherEventsOnDay is provided (detail views).
 *
 * @param event - The meeting event object
 * @param otherEventsOnDay - Optional array of other meetings on the same day.
 *                           When provided, calculates independently (detail views).
 *                           When omitted, fetches from MeetingsContext (list views - requires realtime features).
 * @returns Object with hasRealtimeFeatures flag and warning key
 *
 * @example
 * const { hasRealtimeFeatures, warningKey } = useMeetingWarning(event) // List view
 * const { hasRealtimeFeatures, warningKey } = useMeetingWarning(event, todayMeetings) // Detail view
 */
export const useMeetingWarning = (
  event: Meeting,
  otherEventsOnDay?: Meeting[],
): { hasRealtimeFeatures: boolean; warningKey: WarningKey | null } => {
  const meetingsCtx = useMeetingsContext()
  const { scopeType } = useScope()
  const hasRealtimeFeatures = useMeetingsRealtimeFeaturesEnabled()

  const warningKey = otherEventsOnDay !== undefined
    ? getWarningKey(event, scopeType, { otherEventsOnDay })
    : meetingsCtx?.getWarning?.(event?.uuid) ?? null

  return { hasRealtimeFeatures, warningKey }
}
